import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { fetchAboutPage, fetchGalleryByTitle } from '../helpers/contentfulHelper';
import styles from '../styles/AboutPage.module.css';

const AboutPage = () => {
  const [page, setPage] = useState(null);
  const [galleryEntry, setGalleryEntry] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const load = async () => {
      const data = await fetchAboutPage();
      setPage(data);
    };
    load();
  }, []);

  useEffect(() => {
    const loadGallery = async () => {
      const data = await fetchGalleryByTitle('About Us Gallery');
      setGalleryEntry(data);
    };
    loadGallery();
  }, []);

  const fields = page?.fields || {};

  const title = fields.title || 'Crafted by Three Generations';
  const intro = fields.intro || null;
  const heroImage = fields.heroImage;
  const storyTitle = fields.storyTitle || 'Our Story';
  const storyBody = fields.storyBody || null;
  const values = fields.values || [];
  const galleryCandidates = [
    galleryEntry?.fields?.images,
    fields.gallery,
    fields.galleries,
    fields.photos,
    fields.images,
  ];
  const cta =
    fields.cta ||
    fields.ctaBlock ||
    fields.callToAction ||
    null;
  const ctaTitle = cta?.fields?.title || fields.ctaTitle || 'Planning a project?';
  const ctaText =
    cta?.fields?.text ||
    fields.ctaText ||
    'We’d be happy to discuss ideas, materials, timelines and next steps.';
  const ctaLabel = cta?.fields?.buttonLabel || fields.ctaLabel || 'Make an Enquiry';
  const ctaLink = cta?.fields?.buttonLink || fields.ctaLink || '/contact';
  const ctaIsInternal = typeof ctaLink === 'string' && ctaLink.startsWith('/');
  const hasLightbox = activeIndex !== null;

  const heroImageUrl = heroImage?.fields?.file?.url
    ? `https:${heroImage.fields.file.url}`
    : null;

  const galleryItems = [];
  const seen = new Set();

  const pushAsset = (asset, fallbackAlt = 'About gallery image') => {
    const url = asset?.fields?.file?.url;
    if (!url) return;
    const src = `https:${url}`;
    if (seen.has(src)) return;
    seen.add(src);
    galleryItems.push({
      src,
      alt: asset?.fields?.title || fallbackAlt,
    });
  };

  galleryCandidates.forEach((candidate) => {
    if (!Array.isArray(candidate) || candidate.length === 0) return;

    candidate.forEach((item) => {
      // Direct asset
      if (item?.fields?.file?.url) {
        pushAsset(item);
        return;
      }

      // mediaItem-like entry with nested image asset
      if (item?.fields?.image?.fields?.file?.url) {
        pushAsset(item.fields.image, item?.fields?.title || 'About gallery image');
        return;
      }

      // gallery-like entry with images array of assets
      if (Array.isArray(item?.fields?.images) && item.fields.images.length > 0) {
        item.fields.images.forEach((asset) =>
          pushAsset(asset, item?.fields?.title || 'About gallery image')
        );
      }
    });
  });

  const openLightbox = (index) => setActiveIndex(index);
  const closeLightbox = () => setActiveIndex(null);
  const goPrev = () =>
    setActiveIndex((prev) =>
      prev === null ? prev : prev === 0 ? galleryItems.length - 1 : prev - 1
    );
  const goNext = () =>
    setActiveIndex((prev) =>
      prev === null ? prev : prev === galleryItems.length - 1 ? 0 : prev + 1
    );

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <p className={styles.kicker}>About Us</p>
          <h1>{title}</h1>
          {intro && (
            <div className={styles.richText}>
              {documentToReactComponents(intro)}
            </div>
          )}
        </header>

        {heroImageUrl && (
          <div className={styles.heroImage}>
            <img src={heroImageUrl} alt={heroImage?.fields?.title || 'About Us'} />
          </div>
        )}

        <section className={styles.story}>
          <h2>{storyTitle}</h2>
          {storyBody && (
            <div className={styles.richText}>
              {documentToReactComponents(storyBody)}
            </div>
          )}
        </section>

        {galleryItems.length > 0 && (
          <section className={styles.gallery}>
            {galleryItems.map((item, index) => (
              <button
                key={item.src}
                type="button"
                className={styles.galleryButton}
                onClick={() => openLightbox(index)}
                aria-label={`Open gallery image ${index + 1}`}
              >
                <img src={item.src} alt={item.alt} />
              </button>
            ))}
          </section>
        )}

        {values.length > 0 && (
          <section className={styles.values}>
            {values.map((value) => (
              <article key={value.sys.id}>
                <h3>{value.fields?.title}</h3>
                <p>{value.fields?.body}</p>
              </article>
            ))}
          </section>
        )}

        <section className={styles.cta}>
          <h3>{ctaTitle}</h3>
          <p>{ctaText}</p>
          {ctaIsInternal ? (
            <Link to={ctaLink}>{ctaLabel}</Link>
          ) : (
            <a href={ctaLink}>{ctaLabel}</a>
          )}
        </section>

        {hasLightbox && (
          <div className={styles.lightbox} role="dialog" aria-modal="true">
            <button
              type="button"
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={goPrev}
              aria-label="Previous image"
            >
              &#8249;
            </button>

            <div className={styles.lightboxInner}>
              <button
                type="button"
                className={styles.closeButton}
                onClick={closeLightbox}
                aria-label="Close gallery"
              >
                &#10005;
              </button>

              <img
                src={galleryItems[activeIndex].src}
                alt={galleryItems[activeIndex].alt}
                className={styles.lightboxImage}
              />

              <div className={styles.thumbRow}>
                {galleryItems.map((item, index) => (
                  <button
                    key={`${item.src}-thumb`}
                    type="button"
                    className={`${styles.thumbButton} ${
                      index === activeIndex ? styles.thumbActive : ''
                    }`}
                    onClick={() => openLightbox(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={item.src} alt="" />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={goNext}
              aria-label="Next image"
            >
              &#8250;
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutPage;
